import React, { useEffect, useRef, useState } from "react";
import api, { rupee } from "../../lib/api";
import { toast } from "sonner";
import { BellRing, Play, Upload, Save, Check, Volume2, History } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { SOUND_OPTIONS, playSound, primeAudio } from "../../lib/orderSounds";
import { getAck, isAck, markAck } from "../../lib/orderAlertStore";
import { enablePush, pushSupported } from "../../lib/pushNotifications";

export default function AdminNotifications() {
  const [cfg, setCfg] = useState({ order_alert_enabled: true, order_alert_sound: "chime", order_alert_repeat_minutes: 2, order_alert_custom_sound_url: "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [ackVersion, setAckVersion] = useState(0);
  const fileRef = useRef(null);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const loadHistory = () => api.get("/admin/orders/alerts?limit=100").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
    loadHistory();
    const onAck = () => setAckVersion((v) => v + 1);
    window.addEventListener("bbs-alert-ack", onAck);
    return () => window.removeEventListener("bbs-alert-ack", onAck);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        order_alert_enabled: !!cfg.order_alert_enabled,
        order_alert_sound: cfg.order_alert_sound,
        order_alert_repeat_minutes: Number(cfg.order_alert_repeat_minutes) || 2,
        order_alert_custom_sound_url: cfg.order_alert_custom_sound_url || "",
      });
      toast.success("Notification settings saved");
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  const preview = () => { primeAudio(); playSound(cfg.order_alert_sound, cfg.order_alert_custom_sound_url); };

  const requestPerm = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") {
      toast.success("Browser notifications enabled");
      if (pushSupported()) {
        const res = await enablePush().catch(() => ({ ok: false }));
        if (res.ok) toast.success("Background push enabled — alerts arrive even when this tab is closed");
        else if (res.reason === "not_configured") toast.message("In-tab alerts active (background push not configured on server)");
      }
    } else toast.message("Notifications not enabled in the browser");
  };

  const testAlert = () => {
    primeAudio();
    playSound(cfg.order_alert_sound, cfg.order_alert_custom_sound_url);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("New Order BBS-TEST", { body: "Test Customer · ₹499", tag: "bbs-test" }); } catch (e) {}
    }
    toast.message("Test alert fired");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg((c) => ({ ...c, order_alert_custom_sound_url: data.url, order_alert_sound: "custom" }));
      toast.success("Custom sound uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const acknowledgeOne = async (id) => {
    try { await api.post(`/admin/orders/${id}/acknowledge`); } catch (e) { /* ignore */ }
    markAck([id]);
    loadHistory();
  };
  const acknowledgeAll = async () => {
    for (const h of history) {
      if (!h.acknowledged_at && !isAck(h.id)) {
        try { await api.post(`/admin/orders/${h.id}/acknowledge`); } catch (e) { /* ignore */ }
      }
    }
    markAck(history.map((h) => h.id));
    loadHistory();
    toast.success("All alerts acknowledged & accepted");
  };

  return (
    <div data-testid="admin-notifications" className="max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <BellRing size={24} className="text-gold" />
        <h1 className="font-serif text-3xl text-navy">Order Notification Alert</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Get a browser push + sound the moment a new order arrives — even when this tab is in the background.</p>

      {/* Permission banner */}
      {perm !== "granted" && perm !== "unsupported" && (
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mb-5 flex items-center justify-between gap-3" data-testid="notif-permission-banner">
          <span className="text-sm text-navy">Allow browser notifications to receive order alerts on this device.</span>
          <button onClick={requestPerm} data-testid="enable-notifications-btn" className="shrink-0 bg-navy text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-navy-soft">Enable Notifications</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-black/5 p-6 mb-5 space-y-5" data-testid="notif-settings">
        {/* Enable */}
        <div className="flex items-center justify-between">
          <div><div className="font-medium text-navy">Order Notifications</div><div className="text-xs text-muted-foreground">Turn all new-order alerts on or off.</div></div>
          <Switch checked={!!cfg.order_alert_enabled} onCheckedChange={(v) => set("order_alert_enabled", v)} data-testid="toggle-order-alert" />
        </div>

        {/* Sound */}
        <div>
          <div className="font-medium text-navy mb-2 flex items-center gap-1.5"><Volume2 size={15} className="text-gold" /> Alert Sound</div>
          <div className="flex flex-wrap gap-2">
            {SOUND_OPTIONS.map((s) => (
              <button key={s.key} onClick={() => { set("order_alert_sound", s.key); primeAudio(); playSound(s.key); }}
                data-testid={`sound-${s.key}`} aria-pressed={cfg.order_alert_sound === s.key}
                className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${cfg.order_alert_sound === s.key ? "bg-navy text-cream border-navy" : "bg-white text-navy border-black/15 hover:border-gold"}`}>
                {s.label}
              </button>
            ))}
            {cfg.order_alert_custom_sound_url && (
              <button onClick={() => { set("order_alert_sound", "custom"); preview(); }} data-testid="sound-custom"
                aria-pressed={cfg.order_alert_sound === "custom"}
                className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${cfg.order_alert_sound === "custom" ? "bg-navy text-cream border-navy" : "bg-white text-navy border-black/15 hover:border-gold"}`}>
                Custom (uploaded)
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button onClick={preview} data-testid="preview-sound-btn" className="flex items-center gap-1.5 text-sm font-semibold text-navy border border-navy/20 px-3.5 py-1.5 rounded-full hover:border-gold"><Play size={14} /> Preview</button>
            <button onClick={() => fileRef.current?.click()} data-testid="upload-sound-btn" className="flex items-center gap-1.5 text-sm font-semibold text-navy border border-navy/20 px-3.5 py-1.5 rounded-full hover:border-gold"><Upload size={14} /> Upload custom sound</button>
            <input ref={fileRef} type="file" accept="audio/*" onChange={onUpload} className="hidden" data-testid="sound-file-input" />
          </div>
        </div>

        {/* Repeat interval */}
        <div>
          <div className="font-medium text-navy">Repeat Alert Interval</div>
          <div className="text-xs text-muted-foreground mb-2">If an order is not acknowledged, ring again after this many minutes.</div>
          <div className="flex items-center gap-2">
            <input type="number" min="1" step="1" value={cfg.order_alert_repeat_minutes} onChange={(e) => set("order_alert_repeat_minutes", e.target.value)} data-testid="repeat-minutes-input" className="w-24 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
            <span className="text-sm text-muted-foreground">minutes</span>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={saving} data-testid="save-notif-settings-btn" className="flex items-center gap-1.5 bg-navy text-cream font-semibold px-6 py-2.5 rounded-full hover:bg-navy-soft disabled:opacity-60"><Save size={16} /> {saving ? "Saving..." : "Save Settings"}</button>
          <button onClick={testAlert} data-testid="test-alert-btn" className="flex items-center gap-1.5 text-sm font-semibold text-navy border border-navy/20 px-4 py-2.5 rounded-full hover:border-gold"><BellRing size={15} /> Test Alert</button>
        </div>
      </div>

      {/* History log */}
      <div className="bg-white rounded-xl border border-black/5 p-6" data-testid="notif-history">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-navy flex items-center gap-2"><History size={18} className="text-gold" /> Notification History</h3>
          <button onClick={acknowledgeAll} data-testid="ack-all-btn" className="text-sm font-semibold text-navy border border-navy/20 px-3.5 py-1.5 rounded-full hover:border-gold flex items-center gap-1.5"><Check size={14} /> Acknowledge all</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm" data-testid="history-table">
            <thead className="bg-beige text-navy text-left"><tr><th className="p-3">Time</th><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Acknowledged At</th></tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No order alerts yet.</td></tr>}
              {history.map((h) => (
                <tr key={h.id} className="border-t border-black/5" data-testid={`history-row-${h.order_number}`}>
                  <td className="p-3 text-muted-foreground text-xs">{h.created_at ? new Date(h.created_at).toLocaleString("en-IN") : "—"}</td>
                  <td className="p-3 text-navy font-medium">{h.order_number}</td>
                  <td className="p-3 text-navy">{h.customer_name}</td>
                  <td className="p-3 text-navy">{rupee(h.total)}</td>
                  <td className="p-3">
                    {h.acknowledged_at || isAck(h.id)
                      ? <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Accepted</span>
                      : <button onClick={() => acknowledgeOne(h.id)} data-testid={`ack-${h.order_number}`} className="text-[11px] bg-gold/20 text-navy px-2 py-0.5 rounded-full hover:bg-gold/40">Pending · Acknowledge</button>}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs" data-testid={`ack-time-${h.order_number}`}>{h.acknowledged_at ? new Date(h.acknowledged_at).toLocaleString("en-IN") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <span className="hidden">{ackVersion}{getAck().size}</span>
    </div>
  );
}
